import React, { useContext, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Chip,
  Stack,
  Tab,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import AuthContext from '../../context/AuthContext';
import axios from 'axios';
import { getCoverArtUrl } from '../../utils/urlHelpers';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DashboardPage = () => {
  const { user } = useContext(AuthContext);
  const [tracks, setTracks] = useState([]);
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalTracks: 0,
    totalReleases: 0,
    totalPlays: 0,
    totalDownloads: 0,
    totalComments: 0
  });
  const [tabValue, setTabValue] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [tracksRes, releasesRes, statsRes] = await Promise.all([
          axios.get(`${API_URL}/tracks/myTracks`),
          axios.get(`${API_URL}/releases/myReleases`),
          axios.get(`${API_URL}/tracks/stats`)
        ]);
        
        
        setTracks(tracksRes.data);
        setReleases(releasesRes.data);
        setStats({
          ...statsRes.data,
          totalReleases: releasesRes.data.length
        });
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load dashboard data');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter standalone tracks (not part of a release)
  const singleTracks = tracks.filter(track => !track.release);

  // Filter releases by type
  const singleReleases = releases.filter(release => release.type === 'single');
  const epAlbumReleases = releases.filter(release => release.type !== 'single');

  // Add delete handlers
  const handleDeleteClick = (item, type) => {
    setItemToDelete({ ...item, type });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!itemToDelete) return;

      const endpoint = itemToDelete.type === 'release' 
        ? `${API_URL}/releases/${itemToDelete._id}`
        : `${API_URL}/tracks/${itemToDelete._id}`;

      await axios.delete(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Update state to remove the deleted item
      if (itemToDelete.type === 'release') {
        setReleases(releases.filter(r => r._id !== itemToDelete._id));
      } else {
        setTracks(tracks.filter(t => t._id !== itemToDelete._id));
      }

      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete item');
    }
  };

  const renderTrackRow = (track) => (
    <TableRow key={track._id} hover>
      <TableCell component="th" scope="row">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              mr: 2,
              objectFit: 'cover',
              border: '1px solid',
              borderColor: 'divider',
            }}
            src={getCoverArtUrl(track.coverArt)}
            alt={track.title}
          />
          <Box>
            <Typography variant="subtitle2">{track.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              SINGLE
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>{track.primaryGenre || track.genre}</TableCell>
      <TableCell align="center">{track.playCount || 0}</TableCell>
      <TableCell align="center">{track.downloadCount || 0}</TableCell>
      <TableCell align="center">{track.commentCount || 0}</TableCell>
      <TableCell align="center">
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={<PlayIcon />}
            component={RouterLink}
            to={`/release/${track._id}`}
          >
            View
          </Button>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteClick(track, 'track')}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );

  const renderReleaseRow = (release) => {
    // Calculate total stats for the release by summing up all tracks' stats
    const totalPlays = release.tracks ? release.tracks.reduce((sum, track) => sum + (track.playCount || 0), 0) : 0;
    const totalDownloads = release.tracks ? release.tracks.reduce((sum, track) => sum + (track.downloadCount || 0), 0) : 0;
    const totalComments = release.commentCount || 0;
    
    return (
      <TableRow key={release._id} hover>
        <TableCell component="th" scope="row">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                mr: 2,
                objectFit: 'cover',
                border: '1px solid',
                borderColor: 'divider',
              }}
              src={getCoverArtUrl(release.coverArt)}
              alt={release.title}
            />
            <Box>
              <Typography variant="subtitle2">{release.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {release.type.toUpperCase()} • {release.tracks?.length} tracks
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Chip 
              label={release.primaryGenre} 
              size="small" 
              color="primary"
            />
            {release.secondaryGenres?.map(genre => (
              <Chip 
                key={genre} 
                label={genre} 
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </TableCell>
        <TableCell align="center">{totalPlays}</TableCell>
        <TableCell align="center">{totalDownloads}</TableCell>
        <TableCell align="center">{totalComments}</TableCell>
        <TableCell align="center">
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={<PlayIcon />}
              component={RouterLink}
              to={`/release/${release._id}`}
            >
              View
            </Button>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(release, 'release')}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Container>
      {/* ... existing stats and header code ... */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#e4e4e4' }}>
          Welcome Back, {user?.artistName || user?.username}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your tracks, check feedback, and share with your network.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Quick Stats */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ color: '#e4e4e4' }}>
          Your Stats
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={3}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                textAlign: 'center',
                backgroundColor: '#a187f2',
                color: '#FFFFFF',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
                }
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 600 }}>{stats.totalReleases}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>Releases</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                textAlign: 'center',
                backgroundColor: '#a187f2',
                color: '#FFFFFF',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
                }
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 600 }}>{stats.totalPlays}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>Streams</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                textAlign: 'center',
                backgroundColor: '#a187f2',
                color: '#FFFFFF',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
                }
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 600 }}>{stats.totalDownloads}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>Downloads</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                textAlign: 'center',
                backgroundColor: '#a187f2',
                color: '#FFFFFF',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
                }
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 600 }}>{stats.totalComments}</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>Comments</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Upload Buttons - Show only for artists */}
      {user && (user.accountType === 'artist' || user.isArtist) && (
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/upload/release"
            size="large"
          >
            Create New Release
          </Button>
        </Box>
      )}

      {/* Curator Navigation - Show only for curators */}
      {user && user.accountType === 'curator' && (
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/curator/library"
            size="large"
          >
            My Library
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PlayIcon />}
            component={RouterLink}
            to="/curator/play-logs"
            size="large"
          >
            Play Logs
          </Button>
        </Box>
      )}

      {/* Content Tabs - Show only for artists */}
      {user && (user.accountType === 'artist' || user.isArtist) && (
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <TabList onChange={(_, newValue) => setTabValue(newValue)}>
              <Tab label="All" value="all" />
              <Tab label="Singles" value="singles" />
              <Tab label="EPs & Albums" value="releases" />
            </TabList>
          </Box>

          <TabPanel value="all" sx={{ px: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : singleTracks.length === 0 && releases.length === 0 ? (
              <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <UploadIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {user && (user.accountType === 'artist' || user.isArtist) 
                    ? "No content uploaded yet" 
                    : "No content available"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {user && (user.accountType === 'artist' || user.isArtist)
                    ? "Start by uploading your first track or release." 
                    : "Your curator account lets you download and provide feedback on music."}
                </Typography>
                {user && (user.accountType === 'artist' || user.isArtist) && (
                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    component={RouterLink}
                    to="/upload/release"
                  >
                    Create New Release
                  </Button>
                )}
              </Card>
            ) : (
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Genre</TableCell>
                      <TableCell align="center">Streams</TableCell>
                      <TableCell align="center">Downloads</TableCell>
                      <TableCell align="center">Comments</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Show all releases and standalone tracks */}
                    {releases.map(renderReleaseRow)}
                    {singleTracks.map(renderTrackRow)}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          <TabPanel value="singles" sx={{ px: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : singleTracks.length === 0 && singleReleases.length === 0 ? (
              <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {user && (user.accountType === 'artist' || user.isArtist)
                    ? "No singles uploaded yet."
                    : "No singles available."}
                </Typography>
                {user && (user.accountType === 'artist' || user.isArtist) && (
                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    component={RouterLink}
                    to="/upload/release"
                    sx={{ mt: 2 }}
                  >
                    Create New Release
                  </Button>
                )}
              </Card>
            ) : (
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Genre</TableCell>
                      <TableCell align="center">Streams</TableCell>
                      <TableCell align="center">Downloads</TableCell>
                      <TableCell align="center">Comments</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Show releases with type 'single' and standalone tracks */}
                    {singleReleases.map(renderReleaseRow)}
                    {singleTracks.map(renderTrackRow)}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          <TabPanel value="releases" sx={{ px: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : epAlbumReleases.length === 0 ? (
              <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {user && (user.accountType === 'artist' || user.isArtist)
                    ? "No EPs or Albums uploaded yet."
                    : "No EPs or Albums available."}
                </Typography>
                {user && (user.accountType === 'artist' || user.isArtist) && (
                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    component={RouterLink}
                    to="/upload/release"
                    sx={{ mt: 2 }}
                  >
                    Create New Release
                  </Button>
                )}
              </Card>
            ) : (
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Genre</TableCell>
                      <TableCell align="center">Streams</TableCell>
                      <TableCell align="center">Downloads</TableCell>
                      <TableCell align="center">Comments</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {epAlbumReleases.map(renderReleaseRow)}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </TabContext>
      )}

      {/* Curator Content - Show only for curators */}
      {user && user.accountType === 'curator' && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Curator Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Welcome to your curator dashboard! Here you can manage your music library and track your play logs.
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    My Library
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Access your saved releases and tracks from promo links and comments.
                  </Typography>
                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/curator/library"
                    fullWidth
                  >
                    View Library
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Play Logs
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Track when and where you played music from your library.
                  </Typography>
                  <Button
                    variant="contained"
                    component={RouterLink}
                    to="/curator/play-logs"
                    fullWidth
                  >
                    View Play Logs
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete {itemToDelete?.type === 'release' ? 'Release' : 'Track'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{itemToDelete?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DashboardPage;